// Eventrechner (27.10.2026, Betreiber: „Eventrechner mit CRM“).
// Ein Event mit Ticketkategorien, Szenarien, Sponsoren und Kosten (Plan, Angebot, Ist), dazu der Abgleich mit dem
// Ticketmodul. Die Seite rechnet nichts selbst: alle Zahlen kommen vom Server (backend/eventrechner/logik.py).
// Ticketpreise brutto, Sponsoring und Kosten netto.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { eventrechnerAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PartyPopper, Plus, Save, Trash2, Loader2, RefreshCw, Pencil, Undo2, Link2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { NativeSelect } from '@/components/ui/native-select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const euro = (c, nachkomma = false) => (c == null ? '–' : ((c || 0) / 100).toLocaleString('de-DE', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: nachkomma ? 2 : 0, maximumFractionDigits: nachkomma ? 2 : 0 }));
const zahl = (n) => (n == null ? '–' : n.toLocaleString('de-DE'));
const datumText = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'ohne Datum');

const BLOECKE = [['kampf', 'Kämpfe'], ['location', 'Location & Technik'], ['marketing', 'Marketing'], ['sonstiges', 'Sonstiges']];
const SPONSORING_QUELLE = [
  { value: 'gesichert', label: 'nur gesichert (zugesagt bis bezahlt)' },
  { value: 'offen_mit', label: 'gesichert + im Gespräch' },
  { value: 'ziel', label: 'Sponsoring-Ziel' },
];
const STATUS = {
  angefragt: { label: 'Angefragt', variant: 'outline' },
  im_gespraech: { label: 'Im Gespräch', variant: 'outline' },
  zugesagt: { label: 'Zugesagt', variant: 'secondary' },
  rechnung: { label: 'Rechnung gestellt', variant: 'secondary' },
  bezahlt: { label: 'Bezahlt', variant: 'default' },
  abgesagt: { label: 'Abgesagt', variant: 'destructive' },
};
const SPONSOR_LEER = { firma: '', ansprechpartner: '', telefon: '', email: '', paket: '', art: 'geld', betrag_netto_cent: 0, status: 'angefragt', notiz: '' };

/* Euro-Eingabe in ganzen oder Komma-Beträgen; leer = null (nur wenn erlaubt) */
function EuroFeld({ cent, onChange, leerErlaubt = false, className = '', ...rest }) {
  const [text, setText] = useState(cent == null ? '' : String(cent / 100).replace('.', ','));
  useEffect(() => { setText(cent == null ? '' : String(cent / 100).replace('.', ',')); }, [cent]);
  const fertig = () => {
    const t = text.trim().replace(/\./g, '').replace(',', '.');
    if (!t) { onChange(leerErlaubt ? null : 0); return; }
    const n = Number(t);
    if (Number.isFinite(n) && n >= 0) onChange(Math.round(n * 100));
    else setText(cent == null ? '' : String(cent / 100).replace('.', ','));
  };
  return <Input inputMode="decimal" value={text} onChange={(e) => setText(e.target.value)} onBlur={fertig}
    className={`text-right tabular-nums ${className}`} {...rest} />;
}

export default function EventrechnerPage() {
  const { userPermissions, isAdminPermissions } = useAuth();
  const darf = isAdminPermissions || !!userPermissions?.['eventrechner.edit'];
  const [liste, setListe] = useState([]);
  const [aktivId, setAktivId] = useState(() => { try { return localStorage.getItem('p1_eventrechner') || ''; } catch { return ''; } });
  const [daten, setDaten] = useState(null);
  const [entwurf, setEntwurf] = useState(null);
  const [ticketEvents, setTicketEvents] = useState([]);
  const [laedt, setLaedt] = useState(true);
  const [speichert, setSpeichert] = useState(false);
  const [neu, setNeu] = useState(null);
  const [sponsor, setSponsor] = useState(null);

  const listeLaden = useCallback(async () => {
    try {
      const l = (await eventrechnerAPI.liste()).data;
      setListe(l);
      setAktivId((alt) => (l.some((e) => e.id === alt) ? alt : (l[0]?.id || '')));
    } catch { toast.error('Events konnten nicht geladen werden.'); }
    setLaedt(false);
  }, []);
  useEffect(() => { listeLaden(); eventrechnerAPI.ticketEvents().then((r) => setTicketEvents(r.data)).catch(() => {}); }, [listeLaden]);

  const uebernehmen = (d) => { setDaten(d); setEntwurf(structuredClone(d.event)); };
  const laden = useCallback(async () => {
    if (!aktivId) { setDaten(null); setEntwurf(null); return; }
    try { uebernehmen((await eventrechnerAPI.lesen(aktivId)).data); }
    catch { toast.error('Event konnte nicht geladen werden.'); }
  }, [aktivId]);
  useEffect(() => { laden(); try { localStorage.setItem('p1_eventrechner', aktivId); } catch { /* egal */ } }, [laden, aktivId]);

  const geaendert = useMemo(() => !!(daten && entwurf && JSON.stringify(daten.event) !== JSON.stringify(entwurf)), [daten, entwurf]);
  const setze = (feld, wert) => setEntwurf((e) => ({ ...e, [feld]: wert }));
  const zeileSetzen = (liste_, i, feld, wert) => setEntwurf((e) => ({ ...e, [liste_]: e[liste_].map((z, j) => (j === i ? { ...z, [feld]: wert } : z)) }));
  const zeileWeg = (liste_, i) => setEntwurf((e) => ({ ...e, [liste_]: e[liste_].filter((_, j) => j !== i) }));
  const zeileDazu = (liste_, zeile) => setEntwurf((e) => ({ ...e, [liste_]: [...(e[liste_] || []), zeile] }));

  const speichern = async () => {
    setSpeichert(true);
    try {
      const { name, datum, ort, notiz, ticket_event, mwst_tickets, puffer_prozent, sponsoring_ziel_cent, kategorien, szenarien, kosten } = entwurf;
      const body = { version: daten.event.version, name, datum: datum || undefined, ort, notiz, mwst_tickets, puffer_prozent,
        sponsoring_ziel_cent, kategorien, szenarien, kosten };
      if (ticket_event) body.ticket_event = ticket_event; else if (daten.event.ticket_event) body.ticket_verknuepfung_loesen = true;
      uebernehmen((await eventrechnerAPI.aendern(daten.event.id, body)).data);
      toast.success('Gespeichert.');
      listeLaden();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Speichern fehlgeschlagen. Bitte die Eingaben prüfen.');
    }
    setSpeichert(false);
  };

  const anlegen = async () => {
    try {
      const r = (await eventrechnerAPI.anlegen({ name: neu.name.trim(), datum: neu.datum || undefined, ort: neu.ort || undefined, vorlage: neu.vorlage })).data;
      setNeu(null); await listeLaden(); setAktivId(r.event.id); toast.success('Event angelegt.');
    } catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Anlegen fehlgeschlagen.'); }
  };
  const loeschen = async () => {
    if (!window.confirm(`„${daten.event.name}“ mit allen Sponsoren löschen? Der Ticketverkauf bleibt unberührt.`)) return;
    try { await eventrechnerAPI.loeschen(daten.event.id); setAktivId(''); listeLaden(); toast.success('Gelöscht.'); }
    catch { toast.error('Löschen fehlgeschlagen.'); }
  };
  const sponsorSpeichern = async () => {
    const { id, verlauf, event_id, erstellt_am, geaendert_am, ...body } = sponsor;
    if (!body.email) delete body.email;
    try {
      const r = id ? await eventrechnerAPI.sponsorAendern(daten.event.id, id, body) : await eventrechnerAPI.sponsorAnlegen(daten.event.id, body);
      setDaten(r.data); setSponsor(null); toast.success('Sponsor gespeichert.');
    } catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Bitte Firma und Betrag prüfen.'); }
  };
  const sponsorLoeschen = async (s) => {
    if (!window.confirm(`${s.firma} löschen?`)) return;
    try { setDaten((await eventrechnerAPI.sponsorLoeschen(daten.event.id, s.id)).data); } catch { toast.error('Löschen fehlgeschlagen.'); }
  };

  if (laedt) return <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const r = daten?.rechnung;
  const ist = daten?.tickets;
  const tev = ticketEvents.find((t) => t.slug === entwurf?.ticket_event);

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="eventrechner">
      <div className="flex flex-wrap items-center gap-3">
        <PartyPopper className="h-6 w-6 text-marke" />
        <h1 className="text-2xl font-semibold">Eventrechner</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {liste.length > 0 && (
            <NativeSelect className="w-64" value={aktivId} onChange={(v) => {
              if (geaendert && !window.confirm('Ungespeicherte Änderungen verwerfen?')) return; setAktivId(v);
            }} options={liste.map((e) => ({ value: e.id, label: `${e.name} · ${datumText(e.datum)}` }))} />
          )}
          {darf && <Button onClick={() => setNeu({ name: '', datum: '', ort: '', vorlage: 'kampfabend' })}><Plus className="mr-1 h-4 w-4" />Neues Event</Button>}
        </div>
      </div>

      {!daten && (
        <Card><CardContent className="p-8 text-center text-muted-foreground">
          Noch kein Event angelegt. Mit der Vorlage „Kampfabend“ stehen die Werte der Planung vom 27.10.2026 schon drin:
          500 Plätze, Ø 75,60 €, 20.000 € Sponsoring-Ziel und alle Kostenblöcke.
        </CardContent></Card>
      )}

      {daten && entwurf && (
        <>
          {geaendert && (
            <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-lg border border-marke/40 bg-background/95 p-3 shadow-sm backdrop-blur">
              <span className="text-sm">Ungespeicherte Änderungen. Die Zahlen rechnet der Server nach dem Speichern neu.</span>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEntwurf(structuredClone(daten.event))}><Undo2 className="mr-1 h-4 w-4" />Verwerfen</Button>
                <Button size="sm" onClick={speichern} disabled={speichert}>
                  {speichert ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}Speichern
                </Button>
              </div>
            </div>
          )}

          <Szenarien r={r} />

          <Tabs defaultValue="einstellungen">
            <TabsList className="flex-wrap">
              <TabsTrigger value="einstellungen">Plätze & Szenarien</TabsTrigger>
              <TabsTrigger value="tickets">Ticketverkauf{ist?.verknuepft ? ` (${ist.belegt})` : ''}</TabsTrigger>
              <TabsTrigger value="sponsoren">Sponsoren ({daten.sponsoren.length})</TabsTrigger>
              <TabsTrigger value="kosten">Kosten</TabsTrigger>
            </TabsList>

            <TabsContent value="einstellungen" className="space-y-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Event</CardTitle></CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div><Label>Name</Label><Input value={entwurf.name || ''} disabled={!darf} onChange={(e) => setze('name', e.target.value)} /></div>
                  <div><Label>Datum</Label><Input type="date" value={entwurf.datum || ''} disabled={!darf} onChange={(e) => setze('datum', e.target.value)} /></div>
                  <div><Label>Ort</Label><Input value={entwurf.ort || ''} disabled={!darf} onChange={(e) => setze('ort', e.target.value)} placeholder="z. B. Flashclub Pforzheim" /></div>
                  <div><Label>Sponsoring-Ziel (netto)</Label><EuroFeld cent={entwurf.sponsoring_ziel_cent} disabled={!darf} onChange={(v) => setze('sponsoring_ziel_cent', v)} /></div>
                  <div><Label>MwSt. auf Tickets (%)</Label><Input type="number" min="0" max="25" value={entwurf.mwst_tickets ?? 19} disabled={!darf} onChange={(e) => setze('mwst_tickets', Number(e.target.value))} /></div>
                  <div><Label>Puffer auf offene Kosten (%)</Label><Input type="number" min="0" max="50" value={entwurf.puffer_prozent ?? 10} disabled={!darf} onChange={(e) => setze('puffer_prozent', Number(e.target.value))} /></div>
                  <div className="sm:col-span-2 lg:col-span-3"><Label>Notiz</Label><Textarea rows={2} value={entwurf.notiz || ''} disabled={!darf} onChange={(e) => setze('notiz', e.target.value)} /></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">Ticketkategorien</CardTitle>
                  {darf && <Button size="sm" variant="outline" onClick={() => zeileDazu('kategorien', { name: 'Neue Kategorie', plaetze: 0, preis_brutto_cent: 0, kosten_je_gast_cent: 0, ticket_kategorie: null })}><Plus className="mr-1 h-4 w-4" />Kategorie</Button>}
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <table className="w-full min-w-[620px] text-sm">
                    <thead><tr className="text-left text-muted-foreground">
                      <th className="py-1 pr-2 font-medium">Kategorie</th><th className="w-24 px-2 font-medium text-right">Plätze</th>
                      <th className="w-32 px-2 font-medium text-right">Preis brutto</th><th className="w-32 px-2 font-medium text-right">Kosten je Gast</th>
                      <th className="w-28 px-2 font-medium text-right">voll brutto</th><th className="w-8" />
                    </tr></thead>
                    <tbody>
                      {entwurf.kategorien.map((k, i) => (
                        <tr key={k.id || i} className="border-t">
                          <td className="py-1 pr-2"><Input value={k.name} disabled={!darf} onChange={(e) => zeileSetzen('kategorien', i, 'name', e.target.value)} /></td>
                          <td className="px-2"><Input type="number" min="0" className="text-right" value={k.plaetze} disabled={!darf} onChange={(e) => zeileSetzen('kategorien', i, 'plaetze', Math.max(0, parseInt(e.target.value || '0', 10)))} /></td>
                          <td className="px-2"><EuroFeld cent={k.preis_brutto_cent} disabled={!darf} onChange={(v) => zeileSetzen('kategorien', i, 'preis_brutto_cent', v)} /></td>
                          <td className="px-2"><EuroFeld cent={k.kosten_je_gast_cent} disabled={!darf} onChange={(v) => zeileSetzen('kategorien', i, 'kosten_je_gast_cent', v)} title="z. B. Essen am VIP-Tisch" /></td>
                          <td className="px-2 text-right tabular-nums">{euro(k.plaetze * k.preis_brutto_cent)}</td>
                          <td>{darf && <Button variant="ghost" size="icon" onClick={() => zeileWeg('kategorien', i)}><Trash2 className="h-4 w-4" /></Button>}</td>
                        </tr>
                      ))}
                      <tr className="border-t font-medium">
                        <td className="py-2 pr-2">Gesamt</td>
                        <td className="px-2 text-right tabular-nums">{zahl(entwurf.kategorien.reduce((s, k) => s + (k.plaetze || 0), 0))}</td>
                        <td className="px-2 text-right text-muted-foreground" colSpan={2}>Ø {euro(Math.round(entwurf.kategorien.reduce((s, k) => s + k.plaetze * k.preis_brutto_cent, 0) / Math.max(1, entwurf.kategorien.reduce((s, k) => s + (k.plaetze || 0), 0))), true)}</td>
                        <td className="px-2 text-right tabular-nums">{euro(entwurf.kategorien.reduce((s, k) => s + k.plaetze * k.preis_brutto_cent, 0))}</td><td />
                      </tr>
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">Szenarien</CardTitle>
                  {darf && entwurf.szenarien.length < 8 && <Button size="sm" variant="outline" onClick={() => zeileDazu('szenarien', { name: 'Neu', auslastung_prozent: 80, sponsoring: 'ziel' })}><Plus className="mr-1 h-4 w-4" />Szenario</Button>}
                </CardHeader>
                <CardContent className="space-y-2">
                  {entwurf.szenarien.map((s, i) => (
                    <div key={s.id || i} className="grid items-end gap-2 sm:grid-cols-[1fr_8rem_1.4fr_auto]">
                      <div><Label className="text-xs">Name</Label><Input value={s.name} disabled={!darf} onChange={(e) => zeileSetzen('szenarien', i, 'name', e.target.value)} /></div>
                      <div><Label className="text-xs">Auslastung %</Label><Input type="number" min="0" max="100" className="text-right" value={s.auslastung_prozent} disabled={!darf} onChange={(e) => zeileSetzen('szenarien', i, 'auslastung_prozent', Math.min(100, Math.max(0, Number(e.target.value))))} /></div>
                      <div><Label className="text-xs">Sponsoring</Label><NativeSelect value={s.sponsoring} disabled={!darf} onChange={(v) => zeileSetzen('szenarien', i, 'sponsoring', v)} options={SPONSORING_QUELLE} /></div>
                      <div>{darf && entwurf.szenarien.length > 1 && <Button variant="ghost" size="icon" onClick={() => zeileWeg('szenarien', i)}><Trash2 className="h-4 w-4" /></Button>}</div>
                    </div>
                  ))}
                  {darf && <div className="pt-3"><Button variant="ghost" size="sm" className="text-destructive" onClick={loeschen}><Trash2 className="mr-1 h-4 w-4" />Event löschen</Button></div>}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tickets" className="space-y-4">
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Link2 className="h-4 w-4" />Verknüpfung mit dem Ticketmodul</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="max-w-md"><NativeSelect value={entwurf.ticket_event || ''} disabled={!darf} placeholder="nicht verknüpft"
                    onChange={(v) => setEntwurf((e) => ({ ...e, ticket_event: v || null, kategorien: e.kategorien.map((k) => ({ ...k, ticket_kategorie: null })) }))}
                    options={ticketEvents.map((t) => ({ value: t.slug, label: `${t.name}${t.datum ? ` · ${datumText(t.datum)}` : ''}` }))} /></div>
                  {tev && (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {entwurf.kategorien.map((k, i) => (
                        <div key={k.id || i} className="flex items-center gap-2">
                          <span className="w-40 shrink-0 truncate text-sm">{k.name}</span>
                          <NativeSelect value={k.ticket_kategorie || ''} disabled={!darf} placeholder="– keine –" onChange={(v) => zeileSetzen('kategorien', i, 'ticket_kategorie', v || null)}
                            options={tev.kategorien.map((t) => ({ value: t.id, label: t.name }))} />
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">Der Rechner liest den Verkauf nur. Reservieren, kassieren und stornieren geht weiter auf der Ticketseite.</p>
                </CardContent>
              </Card>
              {ist?.verknuepft && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-base">Ist gegen Plan</CardTitle>
                    <Button variant="ghost" size="sm" onClick={laden}><RefreshCw className="mr-1 h-4 w-4" />Aktualisieren</Button>
                  </CardHeader>
                  <CardContent className="overflow-x-auto">
                    <table className="w-full min-w-[560px] text-sm">
                      <thead><tr className="text-left text-muted-foreground">
                        <th className="py-1 pr-2 font-medium">Kategorie</th><th className="px-2 text-right font-medium">Plätze</th><th className="px-2 text-right font-medium">Belegt</th>
                        <th className="px-2 text-right font-medium">davon bezahlt</th><th className="px-2 text-right font-medium">Kasse</th><th className="px-2 text-right font-medium">Hochrechnung</th>
                      </tr></thead>
                      <tbody>
                        {ist.kategorien.map((z) => (
                          <tr key={z.id} className="border-t">
                            <td className="py-2 pr-2">{z.name}{!z.ticket_kategorie && <span className="ml-2 text-xs text-muted-foreground">nicht zugeordnet</span>}</td>
                            <td className="px-2 text-right tabular-nums">{zahl(z.plaetze)}</td>
                            <td className="whitespace-nowrap px-2 text-right tabular-nums">{z.ticket_kategorie ? `${zahl(z.belegt)} (${z.plaetze ? Math.round(100 * z.belegt / z.plaetze) : 0} %)` : '–'}</td>
                            <td className="px-2 text-right tabular-nums">{z.ticket_kategorie ? zahl(z.bezahlt) : '–'}</td>
                            <td className="px-2 text-right tabular-nums">{z.ticket_kategorie ? euro(z.umsatz_bezahlt) : '–'}</td>
                            <td className="px-2 text-right tabular-nums">{zahl(z.hochrechnung)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="mt-3 text-xs text-muted-foreground">
                      Erster Verkauf {ist.erster_verkauf ? datumText(ist.erster_verkauf) : '–'}{ist.tage_bis_event != null ? `, noch ${ist.tage_bis_event} Tage bis zum Event` : ''}.
                      Die Hochrechnung führt das bisherige Tempo gleichmäßig bis zum Eventtag fort; erfahrungsgemäß kommen die meisten Käufe am Ende, sie ist also eher vorsichtig.
                      {ist.nicht_zugeordnet > 0 && ` ${ist.nicht_zugeordnet} Tickets aus Kategorien ohne Zuordnung zählen nicht mit.`}
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="sponsoren" className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-4">
                <Kachel titel="Ziel" wert={euro(r.sponsoring.ziel)} />
                <Kachel titel="Gesichert" wert={euro(r.sponsoring.gesichert)} hinweis={r.sponsoring.ziel ? `${Math.round(100 * r.sponsoring.gesichert / r.sponsoring.ziel)} % vom Ziel` : null} />
                <Kachel titel="Im Gespräch" wert={euro(r.sponsoring.im_gespraech)} />
                <Kachel titel="Bezahlt" wert={euro(r.sponsoring.bezahlt)} hinweis={r.sponsoring.sachleistung ? `+ ${euro(r.sponsoring.sachleistung)} Sachleistung` : null} />
              </div>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">Sponsoren</CardTitle>
                  {darf && <Button size="sm" onClick={() => setSponsor({ ...SPONSOR_LEER })}><Plus className="mr-1 h-4 w-4" />Sponsor</Button>}
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  {daten.sponsoren.length === 0 ? <p className="text-sm text-muted-foreground">Noch keine Sponsoren erfasst.</p> : (
                    <table className="w-full min-w-[560px] text-sm">
                      <thead><tr className="text-left text-muted-foreground">
                        <th className="py-1 pr-2 font-medium">Firma</th><th className="px-2 font-medium">Paket</th><th className="px-2 font-medium">Stand</th>
                        <th className="px-2 text-right font-medium">Betrag netto</th><th className="w-20" />
                      </tr></thead>
                      <tbody>
                        {daten.sponsoren.map((s) => (
                          <tr key={s.id} className="border-t">
                            <td className="py-2 pr-2"><div className="font-medium">{s.firma}</div>
                              <div className="text-xs text-muted-foreground">{[s.ansprechpartner, s.telefon, s.email].filter(Boolean).join(' · ')}</div></td>
                            <td className="px-2">{s.paket}{s.art === 'sachleistung' && <span className="ml-1 text-xs text-muted-foreground">(Sachleistung)</span>}</td>
                            <td className="px-2"><Badge variant={STATUS[s.status]?.variant}>{STATUS[s.status]?.label || s.status}</Badge></td>
                            <td className="px-2 text-right tabular-nums">{euro(s.betrag_netto_cent)}</td>
                            <td className="text-right">{darf && <>
                              <Button variant="ghost" size="icon" onClick={() => setSponsor({ ...SPONSOR_LEER, ...s, email: s.email || '' })}><Pencil className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => sponsorLoeschen(s)}><Trash2 className="h-4 w-4" /></Button></>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <p className="mt-3 text-xs text-muted-foreground">Gesichert zählt ab „Zugesagt“. Sachleistungen (z. B. Getränke, Druck) stehen getrennt und senken stattdessen die Kosten — dort als Ist eintragen.</p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="kosten" className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-4">
                <Kachel titel="Plan" wert={euro(r.kosten.plan)} />
                <Kachel titel="Erwartet" wert={euro(r.kosten.erwartet)} hinweis={`${r.kosten.abweichung >= 0 ? '+' : '−'} ${euro(Math.abs(r.kosten.abweichung))} zum Plan`} warn={r.kosten.abweichung > 0} />
                <Kachel titel={`Puffer ${entwurf.puffer_prozent ?? 10} %`} wert={euro(r.kosten.puffer)} hinweis="nur auf Posten ohne Ist" />
                <Kachel titel="Bezahlt (Ist)" wert={euro(r.kosten.ist)} />
              </div>
              {BLOECKE.map(([block, titel]) => {
                const summe = r.kosten.bloecke.find((b) => b.block === block);
                const zeilen = entwurf.kosten.map((k, i) => ({ k, i })).filter(({ k }) => k.block === block);
                return (
                  <Card key={block}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0">
                      <CardTitle className="text-base">{titel} <span className="ml-2 text-sm font-normal text-muted-foreground">erwartet {euro(summe?.erwartet)} · Plan {euro(summe?.plan)}</span></CardTitle>
                      {darf && <Button size="sm" variant="outline" onClick={() => zeileDazu('kosten', { block, name: '', plan_cent: 0, angebot_cent: null, ist_cent: null, notiz: '' })}><Plus className="mr-1 h-4 w-4" />Posten</Button>}
                    </CardHeader>
                    <CardContent className="overflow-x-auto">
                      <table className="w-full min-w-[820px] text-sm">
                        <thead><tr className="text-left text-muted-foreground">
                          <th className="w-[16rem] py-1 pr-2 font-medium">Posten</th><th className="w-28 px-2 text-right font-medium">Plan</th>
                          <th className="w-28 px-2 text-right font-medium">Angebot</th><th className="w-28 px-2 text-right font-medium">Ist</th>
                          <th className="px-2 font-medium">Notiz</th><th className="w-8" />
                        </tr></thead>
                        <tbody>
                          {zeilen.map(({ k, i }) => {
                            const drueber = (k.ist_cent ?? k.angebot_cent) != null && (k.ist_cent ?? k.angebot_cent) > k.plan_cent;
                            return (
                              <tr key={k.id || i} className="border-t">
                                <td className="py-1 pr-2"><Input value={k.name} disabled={!darf} placeholder="Bezeichnung" onChange={(e) => zeileSetzen('kosten', i, 'name', e.target.value)} /></td>
                                <td className="px-2"><EuroFeld cent={k.plan_cent} disabled={!darf} onChange={(v) => zeileSetzen('kosten', i, 'plan_cent', v)} /></td>
                                <td className="px-2"><EuroFeld cent={k.angebot_cent} leerErlaubt disabled={!darf} placeholder="–" onChange={(v) => zeileSetzen('kosten', i, 'angebot_cent', v)} /></td>
                                <td className="px-2"><EuroFeld cent={k.ist_cent} leerErlaubt disabled={!darf} placeholder="–" className={drueber ? 'text-destructive' : ''} onChange={(v) => zeileSetzen('kosten', i, 'ist_cent', v)} /></td>
                                <td className="px-2"><Input value={k.notiz || ''} disabled={!darf} onChange={(e) => zeileSetzen('kosten', i, 'notiz', e.target.value)} /></td>
                                <td>{darf && <Button variant="ghost" size="icon" onClick={() => zeileWeg('kosten', i)}><Trash2 className="h-4 w-4" /></Button>}</td>
                              </tr>
                            );
                          })}
                          {zeilen.length === 0 && <tr><td colSpan={6} className="py-2 text-muted-foreground">Keine Posten.</td></tr>}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                );
              })}
              <p className="text-xs text-muted-foreground">Gerechnet wird mit dem besten bekannten Betrag: Ist vor Angebot vor Plan. Beträge netto. Kosten je Gast (z. B. VIP-Essen) stehen bei den Ticketkategorien.</p>
            </TabsContent>
          </Tabs>
        </>
      )}

      <Dialog open={!!neu} onOpenChange={(o) => !o && setNeu(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Neues Event</DialogTitle></DialogHeader>
          {neu && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={neu.name} onChange={(e) => setNeu({ ...neu, name: e.target.value })} placeholder="z. B. P1 Fight Night 2027" /></div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label>Datum</Label><Input type="date" value={neu.datum} onChange={(e) => setNeu({ ...neu, datum: e.target.value })} /></div>
                <div><Label>Ort</Label><Input value={neu.ort} onChange={(e) => setNeu({ ...neu, ort: e.target.value })} placeholder="Flashclub Pforzheim" /></div>
              </div>
              <div><Label>Vorlage</Label><NativeSelect value={neu.vorlage} onChange={(v) => setNeu({ ...neu, vorlage: v })} options={[
                { value: 'kampfabend', label: 'Kampfabend: 500 Plätze, Kostenblöcke, 20.000 € Sponsoring-Ziel' },
                { value: 'leer', label: 'Leer' }]} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={anlegen} disabled={!neu || neu.name.trim().length < 2}>Anlegen</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sponsor} onOpenChange={(o) => !o && setSponsor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{sponsor?.id ? 'Sponsor bearbeiten' : 'Neuer Sponsor'}</DialogTitle></DialogHeader>
          {sponsor && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label>Firma</Label><Input value={sponsor.firma} onChange={(e) => setSponsor({ ...sponsor, firma: e.target.value })} /></div>
              <div><Label>Ansprechpartner</Label><Input value={sponsor.ansprechpartner} onChange={(e) => setSponsor({ ...sponsor, ansprechpartner: e.target.value })} /></div>
              <div><Label>Telefon</Label><Input value={sponsor.telefon} onChange={(e) => setSponsor({ ...sponsor, telefon: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>E-Mail</Label><Input type="email" value={sponsor.email} onChange={(e) => setSponsor({ ...sponsor, email: e.target.value })} /></div>
              <div><Label>Paket</Label><Input value={sponsor.paket} placeholder="Gold, Cage-Pad, Bande …" onChange={(e) => setSponsor({ ...sponsor, paket: e.target.value })} /></div>
              <div><Label>Art</Label><NativeSelect value={sponsor.art} onChange={(v) => setSponsor({ ...sponsor, art: v })} options={[{ value: 'geld', label: 'Geld' }, { value: 'sachleistung', label: 'Sachleistung' }]} /></div>
              <div><Label>Betrag netto</Label><EuroFeld cent={sponsor.betrag_netto_cent} onChange={(v) => setSponsor({ ...sponsor, betrag_netto_cent: v })} /></div>
              <div><Label>Stand</Label><NativeSelect value={sponsor.status} onChange={(v) => setSponsor({ ...sponsor, status: v })} options={Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label }))} /></div>
              <div className="sm:col-span-2"><Label>Notiz</Label><Textarea rows={2} value={sponsor.notiz} onChange={(e) => setSponsor({ ...sponsor, notiz: e.target.value })} /></div>
              {sponsor.verlauf?.length > 1 && (
                <div className="text-xs text-muted-foreground sm:col-span-2">Verlauf: {sponsor.verlauf.map((v) => `${STATUS[v.status]?.label || v.status} ${new Date(v.am).toLocaleDateString('de-DE')}`).join(' → ')}</div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={sponsorSpeichern} disabled={!sponsor || sponsor.firma.trim().length < 2}>Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kachel({ titel, wert, hinweis, warn }) {
  return (
    <Card><CardContent className="p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{titel}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{wert}</div>
      {hinweis && <div className={`mt-1 text-xs ${warn ? 'text-destructive' : 'text-muted-foreground'}`}>{hinweis}</div>}
    </CardContent></Card>
  );
}

/* Szenarien nebeneinander: dieselben Zeilen für jedes Szenario, Gewinn farbig */
function Szenarien({ r }) {
  const s = r.szenarien;
  const zeilen = [
    ['Gäste', (x) => zahl(x.gaeste)],
    ['Ø Ticket brutto', (x) => euro(x.schnitt_brutto, true)],
    ['Tickets brutto', (x) => euro(x.tickets_brutto)],
    ['Tickets netto', (x) => euro(x.tickets_netto)],
    ['Sponsoring netto', (x) => euro(x.sponsoring)],
    ['Umsatz netto', (x) => euro(x.umsatz_netto), true],
    ['Fixkosten inkl. Puffer', (x) => euro(x.kosten_fix)],
    ['Kosten je Gast', (x) => euro(x.kosten_variabel)],
    ['Kosten gesamt', (x) => euro(x.kosten), true],
  ];
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Ergebnis je Szenario</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead><tr>
            <th />
            {s.map((x) => (
              <th key={x.id} className="whitespace-nowrap px-3 pb-2 text-right font-medium">
                {x.name}
                <div className="text-xs font-normal text-muted-foreground">{x.automatisch ? 'aus dem Verkauf' : `${zahl(x.auslastung_prozent)} % Auslastung`}</div>
              </th>
            ))}
          </tr></thead>
          <tbody>
            {zeilen.map(([titel, f, fett]) => (
              <tr key={titel} className="border-t">
                <td className={`whitespace-nowrap py-1.5 pr-3 ${fett ? 'font-medium' : 'text-muted-foreground'}`}>{titel}</td>
                {s.map((x) => <td key={x.id} className={`whitespace-nowrap px-3 text-right tabular-nums ${fett ? 'font-medium' : ''}`}>{f(x)}</td>)}
              </tr>
            ))}
            <tr className="border-t-2">
              <td className="py-2 pr-3 font-semibold">Gewinn</td>
              {s.map((x) => <td key={x.id} className={`px-3 text-right text-lg font-semibold tabular-nums ${x.gewinn < 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}>{euro(x.gewinn)}</td>)}
            </tr>
            <tr className="border-t">
              <td className="py-1.5 pr-3 text-muted-foreground">Break-even</td>
              {s.map((x) => (
                <td key={x.id} className="whitespace-nowrap px-3 text-right text-xs tabular-nums">
                  {x.break_even.gaeste == null ? 'nicht erreichbar' : `${zahl(x.break_even.gaeste)} Gäste (${zahl(x.break_even.auslastung)} %)`}
                  {x.break_even.gaeste != null && !x.break_even.erreichbar && <div className="text-destructive">mehr als Plätze</div>}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="mt-3 text-xs text-muted-foreground">Ohne Barumsatz. Break-even = Gäste im Platz-Mix, ab denen der Gewinn null ist, mit dem Sponsoring des Szenarios.</p>
      </CardContent>
    </Card>
  );
}
